import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Paginator, TableSpinner } from "../../../../components";
import { SubscriptionBasicInformationDto } from "../../../../interfaces";
import { useGetStudentSubscriptionsQuery } from "../../../../store/api/studentApi";

const PAGE_SIZE = 8;

type TProps = {
  studentId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  onChangeSubscription: (subscription: SubscriptionBasicInformationDto) => void;
};

const localized = (
  value: { en?: string; ar?: string } | null | undefined,
  lang: string,
) => (lang === "ar" ? value?.ar : value?.en) || value?.en || value?.ar || "—";

const calendarDay = (value: Date | string) => {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const subscriptionPeriod = (
  startAt?: Date | string | null,
  endAt?: Date | string | null,
) => {
  if (!startAt || !endAt) return "past";
  const today = new Date();
  const todayDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  ).getTime();
  if (calendarDay(endAt) < todayDay) return "past";
  if (calendarDay(startAt) > todayDay) return "upcoming";
  return "ongoing";
};

export const StudentsSubscriptionsModal: React.FC<TProps> = ({
  studentId,
  show,
  onCloseClick,
  onChangeSubscription,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [pageState, setPageState] = useState({ studentId, open: show, page: 1 });
  const resetPage =
    studentId !== pageState.studentId || (show && !pageState.open);

  if (resetPage) {
    setPageState({ studentId, open: show, page: 1 });
  } else if (show !== pageState.open) {
    setPageState((current) => ({ ...current, open: show }));
  }

  const page = resetPage ? 1 : pageState.page;
  const { data, isLoading, isFetching, isError } = useGetStudentSubscriptionsQuery(
    { studentId, page, limit: PAGE_SIZE },
    { skip: !show || !studentId },
  );

  const subscriptions = data?.data ?? [];
  const meta = data?.meta;
  const totalItems = meta?.totalItems ?? 0;

  const onClose = () => {
    onCloseClick(!show);
  };

  return (
    <React.Fragment>
      <Modal
        size="xl"
        id="subscriptions"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        scrollable
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {t("subscriptions")} {`(${totalItems})`}
        </ModalHeader>
        <ModalBody>
          {isLoading || (isFetching && subscriptions.length === 0) ? (
            <TableSpinner />
          ) : isError ? (
            <p className="text-danger text-center mb-0">{t("error")}</p>
          ) : subscriptions.length === 0 ? (
            <p className="text-muted text-center mb-0">{t("noRecordsFound")}</p>
          ) : (
            <Row>
              {subscriptions.map(
                (subscription: SubscriptionBasicInformationDto) => {
                  const period = subscriptionPeriod(
                    subscription.startAt,
                    subscription.endAt,
                  );
                  const path = [
                    localized(subscription.educationSystem?.name, lang),
                    localized(subscription.curriculum?.name, lang),
                    localized(subscription.course?.name, lang),
                    localized(subscription.educationalStage?.name, lang),
                    localized(subscription.grade?.name, lang),
                  ]
                    .filter((part) => part && part !== "—")
                    .join(" / ");

                  return (
                    <Col
                      xs={6}
                      sm={6}
                      lg={3}
                      className="py-2"
                      key={subscription.subscriptionId}
                    >
                      <div className="border rounded h-100 p-3 d-flex flex-column">
                        <div className="text-center flex-grow-1">
                          <h5 className="fs-16 mb-2">
                            {localized(subscription.name, lang)}
                          </h5>
                          <div className="mb-2">
                            <span
                              className={`badge ${
                                subscription.purchaseKind === "upgrade"
                                  ? "bg-warning text-dark"
                                  : subscription.purchaseKind === "replacement"
                                    ? "bg-secondary"
                                    : "bg-success"
                              }`}
                            >
                              {subscription.purchaseKind === "upgrade"
                                ? t("purchaseUpgraded")
                                : subscription.purchaseKind === "replacement"
                                  ? t("purchaseReplacement")
                                  : t("purchaseSubscribed")}
                            </span>
                            {period === "past" && (
                              <span className="badge bg-danger ms-1">
                                {t("subscriptionExpired")}
                              </span>
                            )}
                            {subscription.sequenceNumber != null && (
                              <small className="text-muted d-block mt-1">
                                {t("planRankLabel", {
                                  rank: subscription.sequenceNumber,
                                })}
                              </small>
                            )}
                          </div>
                          {path ? (
                            <small className="text-muted d-block mb-2 text-wrap">
                              {path}
                            </small>
                          ) : (
                            <>
                              <h6 className="fs-14 mb-1">
                                {localized(subscription.course?.name, lang)}
                              </h6>
                              <h6 className="fs-14 mb-2">
                                {localized(subscription.grade?.name, lang)}
                              </h6>
                            </>
                          )}
                          {subscription.startAt && subscription.endAt && (
                            <h6 className="fs-12 mb-1 text-muted">
                              {new Date(subscription.startAt).toLocaleDateString()}{" "}
                              - {new Date(subscription.endAt).toLocaleDateString()}
                            </h6>
                          )}
                        </div>
                        {period === "ongoing" ? (
                          <Button
                            className="btn btn-success btn-sm mt-2"
                            onClick={() => {
                              onChangeSubscription(subscription);
                            }}
                          >
                            {t("update")}
                          </Button>
                        ) : (
                          <small className="text-muted text-center mt-2">
                            {period === "past"
                              ? t("subscriptionExpired")
                              : t("subscriptionNotStarted")}
                          </small>
                        )}
                      </div>
                    </Col>
                  );
                },
              )}
            </Row>
          )}
        </ModalBody>
        {meta && meta.totalPages > 1 && (
          <ModalFooter className="d-block">
            <Paginator
              itemsPerPage={meta.itemsPerPage}
              totalItems={meta.totalItems}
              currentPage={meta.currentPage}
              totalPages={meta.totalPages}
              itemsOnCurrentPage={subscriptions.length}
              onChangePage={(nextPage) =>
                setPageState({ studentId, open: show, page: nextPage })
              }
            />
          </ModalFooter>
        )}
      </Modal>
    </React.Fragment>
  );
};
